import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, TrendingUp, PieChart } from 'lucide-react';
import StatCard from '../components/StatCard';
import CategorySummary from '../components/CategorySummary';
import ExpenseList from '../components/ExpenseList';
import Navbar from '../components/Navbar';
import { useAuth } from '../utils/AuthContext';
import ExpenseService from '../services/ExpenseService';
import CategoryService from '../services/CategoryService';
import { getCategoryGradient } from '../utils/CategoryColors';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ClearAllConfirmationModal from '../components/ClearAllConfirmationModal';

const Dashboard = () => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [categories, setCategories] = useState([]); // State for dynamic categories
  const [deleteExpenseId, setDeleteExpenseId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openClearAllDialog, setOpenClearAllDialog] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const fetchedExpenses = await ExpenseService.getExpenses();
        setExpenses(fetchedExpenses);
        calculateSummary(fetchedExpenses);

        const fetchedCategories = await CategoryService.getCategories();
        setCategories(fetchedCategories.map(cat => ({ value: cat, label: cat })));
      } catch (error) {
        console.error('Error fetching data for dashboard:', error);
      }
    };

    fetchData();
  }, [navigate, isAuthenticated]);

  const calculateSummary = (expenseList) => {
    const summaryData = expenseList.reduce((acc, expense) => {
      acc[expense.category] = (acc[expense.category] || 0) + expense.amount;
      return acc;
    }, {});
    setSummary(summaryData);
  };

  const handleDeleteClick = (id) => {
    setDeleteExpenseId(id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await ExpenseService.deleteExpense(deleteExpenseId);
      const updatedExpenses = expenses.filter(exp => exp._id !== deleteExpenseId);
      setExpenses(updatedExpenses);
      calculateSummary(updatedExpenses);
      setOpenDeleteDialog(false);
      setDeleteExpenseId(null);
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const confirmClearAll = async () => {
    try {
      await ExpenseService.clearAllExpenses();
      setExpenses([]);
      setSummary({});
      setOpenClearAllDialog(false);
    } catch (error) {
      console.error('Error clearing all expenses:', error);
    }
  };

  const totalSpending = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const avgExpense = expenses.length > 0 ? totalSpending / expenses.length : 0;

  const recentExpenses = expenses.slice(0, 3); // Display only a few recent expenses

  const handleExpenseClick = (expenseId) => {
    navigate(`/transactions`); // Changed to simply navigate to /all-transactions
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 font-sans relative overflow-hidden">
      <Navbar />
      <br />
      <br />
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl text-gray-800 mb-8 text-center">Dashboard</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard
            icon={DollarSign}
            title="Total Spending"
            value={`₹${totalSpending.toFixed(2)}`}
            subtitle={`${expenses.length} transactions`}
            gradientFrom="from-emerald-400"
            gradientTo="to-green-600"
          />

          <StatCard
            icon={TrendingUp}
            title="Average"
            value={`₹${avgExpense.toFixed(2)}`}
            subtitle="per transaction"
            gradientFrom="from-blue-400"
            gradientTo="to-indigo-600"
          />

          <StatCard
            icon={PieChart}
            title="Categories"
            value={Object.keys(summary).length}
            subtitle="active categories"
            gradientFrom="from-purple-400"
            gradientTo="to-pink-600"
          />
        </div>

        {/* Category Summary */}
        <div className="mb-8">
          <CategorySummary
            summary={summary}
            totalSpending={totalSpending}
            categories={categories}
            getCategoryGradient={getCategoryGradient}
          />
        </div>

        {/* Recent Expenses (simplified ExpenseList) */}
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Recent Transactions</h2>
        <ExpenseList
          expenses={recentExpenses}
          filteredExpenses={recentExpenses}
          filterCategory=""
          setFilterCategory={() => {}}
          categories={categories}
          getCategoryGradient={getCategoryGradient}
          handleDeleteClick={handleDeleteClick}
          setOpenClearAllDialog={setOpenClearAllDialog}
          onExpenseClick={handleExpenseClick} // Pass the click handler
        />
      </div>

      <DeleteConfirmationModal
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDelete}
      />

      <ClearAllConfirmationModal
        open={openClearAllDialog}
        onClose={() => setOpenClearAllDialog(false)}
        onConfirm={confirmClearAll}
      />
    </div>
  );
};

export default Dashboard;
